import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import CourtCard from "@/components/CourtCard";
import MapView from "@/components/MapView";
import { SPORT_COLORS, hexToRgba, CITIES } from "@/lib/utils";
import { SlidersHorizontal, X, Search as SearchIcon, MapPin, Navigation, Clock } from "lucide-react";
import { toast } from "sonner";

const SPORTS = ["all", "football", "basketball", "tennis", "padel", "pickleball"];

const COURT_TYPES = {
  football: [{ value: "5-a-side", labelKey: "owner.court_form.court_type_5_a_side" }, { value: "6-a-side", labelKey: "owner.court_form.court_type_6_a_side" }, { value: "7-a-side", labelKey: "owner.court_form.court_type_7_a_side" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  basketball: [{ value: "half", labelKey: "owner.court_form.court_type_half" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  tennis: [{ value: "mini", labelKey: "owner.court_form.court_type_mini" }, { value: "standard", labelKey: "owner.court_form.court_type_standard" }, { value: "full", labelKey: "owner.court_form.court_type_full" }],
  padel: [{ value: "standard", labelKey: "owner.court_form.court_type_standard" }],
  pickleball: [{ value: "standard", labelKey: "owner.court_form.court_type_standard" }],
};

const COURT_SURFACES = {
  football: [{ value: "grass", labelKey: "owner.court_form.surface_football_grass" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_football_artificial_grass" }, { value: "hard", labelKey: "owner.court_form.surface_football_hard" }, { value: "parquet", labelKey: "owner.court_form.surface_football_parquet" }],
  basketball: [{ value: "parquet", labelKey: "owner.court_form.surface_basketball_parquet" }, { value: "hard", labelKey: "owner.court_form.surface_basketball_hard" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_basketball_artificial_grass" }],
  tennis: [{ value: "hard", labelKey: "owner.court_form.surface_tennis_hard" }, { value: "clay", labelKey: "owner.court_form.surface_tennis_clay" }, { value: "grass", labelKey: "owner.court_form.surface_tennis_grass" }, { value: "artificial_grass", labelKey: "owner.court_form.surface_tennis_artificial_grass" }, { value: "carpet", labelKey: "owner.court_form.surface_tennis_carpet" }, { value: "parquet", labelKey: "owner.court_form.surface_tennis_parquet" }],
  padel: [{ value: "artificial_grass", labelKey: "owner.court_form.surface_padel_artificial_grass" }, { value: "hard", labelKey: "owner.court_form.surface_padel_hard" }, { value: "concrete", labelKey: "owner.court_form.surface_padel_concrete" }],
  pickleball: [{ value: "hard", labelKey: "owner.court_form.surface_pickleball_hard" }, { value: "parquet", labelKey: "owner.court_form.surface_pickleball_parquet" }, { value: "concrete", labelKey: "owner.court_form.surface_pickleball_concrete" }],
};

const SORT_OPTIONS = [
  { value: "", label_key: "search.sort_by" },
  { value: "distance", label_key: "search.sort_distance" },
  { value: "price_asc", label_key: "search.sort_price_asc" },
  { value: "price_desc", label_key: "search.sort_price_desc" },
  { value: "rating", label_key: "search.sort_rating" },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "online", labelKey: "court.pay_online" },
  { value: "cod", labelKey: "court.pay_cod" },
  { value: "efawateercom", labelKey: "court.pay_efawateercom" },
];

export default function Search() {
  const { t } = useTranslation();
  const [params, setSearchParams] = useSearchParams();

  const urlInit = useMemo(() => ({
    sport: params.get("sport") || "all",
    sort: params.get("sort") || "",
    timeDate: params.get("date") || "",
    timeFrom: params.get("start_hour") || "",
    timeTo: params.get("end_hour") || "",
    indoor: params.get("indoor") === "true" ? "indoor" : params.get("indoor") === "false" ? "outdoor" : "any",
    selectedAmenities: params.get("amenities") ? params.get("amenities").split(",") : [],
    selectedPaymentMethods: params.get("payment_methods") ? params.get("payment_methods").split(",") : [],
    city: params.get("city") || "",
    search: params.get("search") || "",
    north: params.get("north"),
    south: params.get("south"),
    east: params.get("east"),
    west: params.get("west"),
    zoom: params.get("zoom") ? Number(params.get("zoom")) : null,
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    court_type: params.get("court_type") || "",
    court_surface: params.get("court_surface") || "",
    open_now: params.get("open_now") || "",
  }), [params]);

  const hasUrlBounds = urlInit.north && urlInit.south && urlInit.east && urlInit.west;
  const urlBoundsCenter = hasUrlBounds ? {
    lat: ((Number(urlInit.north) + Number(urlInit.south)) / 2).toFixed(4),
    lng: ((Number(urlInit.east) + Number(urlInit.west)) / 2).toFixed(4),
  } : null;

  const [courts, setCourts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bounds, setBounds] = useState(hasUrlBounds ? { north: Number(urlInit.north), south: Number(urlInit.south), east: Number(urlInit.east), west: Number(urlInit.west) } : null);
  const [mapCenter, setMapCenter] = useState(urlBoundsCenter);
  const [mapZoom, setMapZoom] = useState(urlInit.zoom || 11);
  const [sport, setSport] = useState(urlInit.sport);
  const [indoor, setIndoor] = useState(urlInit.indoor);
  const [selectedAmenities, setSelectedAmenities] = useState(urlInit.selectedAmenities);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState(urlInit.selectedPaymentMethods);
  const [sort, setSort] = useState(urlInit.sort);
  const [showFilters, setShowFilters] = useState(false);
  const [allAmenities, setAllAmenities] = useState([]);
  const [panelOpen, setPanelOpen] = useState(true);
  const [nearMeLoading, setNearMeLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [timeDate, setTimeDate] = useState(urlInit.timeDate);
  const [timeFrom, setTimeFrom] = useState(urlInit.timeFrom);
  const [timeTo, setTimeTo] = useState(urlInit.timeTo);
  const [searchQuery, setSearchQuery] = useState(urlInit.search);
  const [minPrice, setMinPrice] = useState(urlInit.min_price || "");
  const [maxPrice, setMaxPrice] = useState(urlInit.max_price || "");
  const [courtType, setCourtType] = useState(urlInit.court_type || "");
  const [courtSurface, setCourtSurface] = useState(urlInit.court_surface || "");
  const [openNow, setOpenNow] = useState(urlInit.open_now === "true");
  const debounceRef = useRef(null);
  const mapRef = useRef(null);
  const initialLoadDone = useRef(false);
  const urlSyncRef = useRef(null);
  const autoLocateAttempted = useRef(false);

  useEffect(() => {
    api.get("/amenities").then((r) => setAllAmenities(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  useEffect(() => {
    if (urlSyncRef.current) clearTimeout(urlSyncRef.current);
    urlSyncRef.current = setTimeout(() => {
      const q = new URLSearchParams();
      if (sport !== "all") q.set("sport", sport);
      if (sort) q.set("sort", sort);
      if (timeDate) q.set("date", timeDate);
      if (timeFrom) q.set("start_hour", timeFrom);
      if (timeTo) q.set("end_hour", timeTo);
      if (indoor === "indoor") q.set("indoor", "true");
      if (indoor === "outdoor") q.set("indoor", "false");
      if (selectedAmenities.length > 0) q.set("amenities", selectedAmenities.join(","));
      if (selectedPaymentMethods.length > 0) q.set("payment_methods", selectedPaymentMethods.join(","));
      if (searchQuery) q.set("search", searchQuery);
      if (minPrice) q.set("min_price", String(minPrice));
      if (maxPrice) q.set("max_price", String(maxPrice));
      if (courtType) q.set("court_type", courtType);
      if (courtSurface) q.set("court_surface", courtSurface);
      if (openNow) q.set("open_now", "true");
      if (bounds) {
        q.set("north", bounds.north.toFixed(4));
        q.set("south", bounds.south.toFixed(4));
        q.set("east", bounds.east.toFixed(4));
        q.set("west", bounds.west.toFixed(4));
        q.set("zoom", String(mapZoom));
      }
      setSearchParams(q, { replace: true });
    }, 400);
  }, [sport, sort, timeDate, timeFrom, timeTo, indoor, selectedAmenities, selectedPaymentMethods, bounds, mapZoom, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow, setSearchParams]);

  const buildQuery = useCallback((bnds, centerPos, sprt, indr, amenities, paymentMethods, srt, td, tf, tt, sq, minP, maxP, cType, cSurface, oNow) => {
    const q = new URLSearchParams();
    if (sprt !== "all") q.set("sport", sprt);
    if (indr === "indoor") q.set("indoor", "true");
    if (indr === "outdoor") q.set("indoor", "false");
    if (amenities && amenities.length > 0) q.set("amenities", amenities.join(","));
    if (paymentMethods && paymentMethods.length > 0) q.set("payment_methods", paymentMethods.join(","));
    if (srt) q.set("sort", srt);
    if (td) q.set("date", td);
    if (tf) q.set("start_hour", tf);
    if (tt) q.set("end_hour", tt);
    if (sq) q.set("search", sq);
    if (minP) q.set("min_price", minP);
    if (maxP) q.set("max_price", maxP);
    if (cType) q.set("court_type", cType);
    if (cSurface) q.set("court_surface", cSurface);
    if (oNow) q.set("open_now", "true");
    if (bnds) {
      q.set("north", bnds.north);
      q.set("south", bnds.south);
      q.set("east", bnds.east);
      q.set("west", bnds.west);
    }
    if (centerPos) {
      q.set("user_lat", centerPos.lat);
      q.set("user_lng", centerPos.lng);
    }
    q.set("limit", "50");
    return q;
  }, []);

  const loadCourts = useCallback(async (bnds, centerPos, sprt, indr, amenities, paymentMethods, srt, td, tf, tt, sq, minP, maxP, cType, cSurface, oNow) => {
    setLoading(true);
    const q = buildQuery(bnds, centerPos, sprt, indr, amenities, paymentMethods, srt, td, tf, tt, sq, minP, maxP, cType, cSurface, oNow);
    try {
      const r = await api.get(`/courts?${q.toString()}`);
      setCourts(r.data.courts || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const handleMove = useCallback((map) => {
    const c = map.getCenter();
    const b = map.getBounds();
    const newBounds = {
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    };
    const newCenter = { lat: c.lat.toFixed(4), lng: c.lng.toFixed(4) };
    setBounds(newBounds);
    setMapCenter(newCenter);
    setMapZoom(map.getZoom());

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadCourts(newBounds, newCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow);
    }, 500);
  }, [sport, indoor, selectedAmenities, selectedPaymentMethods, sort, loadCourts, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow]);

  const flyTo = useCallback((lat, lng, zoom = 13) => {
    if (mapRef.current) {
      mapRef.current.flyTo([lat, lng], zoom, { duration: 1.5 });
    }
  }, []);

  const handleCitySelect = useCallback((cityName) => {
    const city = CITIES.find((c) => c.key === cityName);
    if (city) {
      flyTo(city.lat, city.lng);
    }
  }, [flyTo]);

  const handleSearchSubmit = useCallback((val) => {
    setSearchQuery(val);
    loadCourts(bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, timeDate, timeFrom, timeTo, val, minPrice, maxPrice, courtType, courtSurface, openNow);
  }, [bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, timeDate, timeFrom, timeTo, loadCourts, minPrice, maxPrice, courtType, courtSurface, openNow]);

  const resolveUserLocation = useCallback(async ({ silent = false } = {}) => {
    const tryIpGeo = async () => {
      try {
        const r = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) });
        const data = await r.json();
        if (data.latitude != null && data.longitude != null) {
          const nextLocation = { lat: data.latitude, lng: data.longitude };
          setUserLocation(nextLocation);
          flyTo(data.latitude, data.longitude);
          return nextLocation;
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn("IP geolocation failed:", e);
      }
      if (!silent) toast.error(t("common.could_not_get_location"));
      return null;
    };

    if (!navigator.geolocation) {
      return tryIpGeo();
    }

    try {
      if (navigator.permissions?.query) {
        const permission = await navigator.permissions.query({ name: "geolocation" });
        if (permission.state !== "granted") {
          return tryIpGeo();
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn("Geolocation permission query failed:", e);
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const nextLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(nextLocation);
          flyTo(pos.coords.latitude, pos.coords.longitude);
          resolve(nextLocation);
        },
        async () => {
          resolve(await tryIpGeo());
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    });
  }, [flyTo, t]);

  const handleNearMe = useCallback(async () => {
    setNearMeLoading(true);
    try {
      await resolveUserLocation();
    } finally {
      setNearMeLoading(false);
    }
  }, [resolveUserLocation]);

  const handleSportChange = useCallback((s) => {
    setSport(s);
    setCourtType("");
    setCourtSurface("");
    loadCourts(bounds, mapCenter, s, indoor, selectedAmenities, selectedPaymentMethods, sort, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, "", "", openNow);
  }, [bounds, mapCenter, indoor, selectedAmenities, selectedPaymentMethods, sort, loadCourts, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, openNow]);

  const handleSortChange = useCallback((s) => {
    setSort(s);
    loadCourts(bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, s, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow);
  }, [bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, loadCourts, timeDate, timeFrom, timeTo, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow]);

  const applyFilters = useCallback((indr, amenities, paymentMethods, minP, maxP, cType, cSurface, oNow) => {
    setIndoor(indr);
    setSelectedAmenities(amenities);
    setSelectedPaymentMethods(paymentMethods);
    setMinPrice(minP);
    setMaxPrice(maxP);
    setCourtType(cType);
    setCourtSurface(cSurface);
    setOpenNow(oNow);
    loadCourts(bounds, mapCenter, sport, indr, amenities, paymentMethods, sort, timeDate, timeFrom, timeTo, searchQuery, minP, maxP, cType, cSurface, oNow);
    setShowFilters(false);
  }, [bounds, mapCenter, sport, sort, loadCourts, timeDate, timeFrom, timeTo, searchQuery]);

  const handleMapReady = useCallback((map) => {
    mapRef.current = map;

    if (hasUrlBounds) {
      const lat = (Number(urlInit.north) + Number(urlInit.south)) / 2;
      const lng = (Number(urlInit.east) + Number(urlInit.west)) / 2;
      map.setView([lat, lng], urlInit.zoom || 11, { animate: false });
    }

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      const c = map.getCenter();
      const b = map.getBounds();
      if (!bounds) {
        setBounds({ north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() });
        setMapCenter({ lat: c.lat.toFixed(4), lng: c.lng.toFixed(4) });
        setMapZoom(map.getZoom());
      }
      loadCourts(bounds || { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() }, mapCenter || { lat: c.lat.toFixed(4), lng: c.lng.toFixed(4) }, urlInit.sport, urlInit.indoor, urlInit.selectedAmenities, urlInit.selectedPaymentMethods, urlInit.sort, urlInit.timeDate, urlInit.timeFrom, urlInit.timeTo, urlInit.search, urlInit.min_price, urlInit.max_price, urlInit.court_type, urlInit.court_surface, urlInit.open_now === "true");

      if (!hasUrlBounds && !urlInit.city && !autoLocateAttempted.current) {
        autoLocateAttempted.current = true;
        resolveUserLocation({ silent: true });
      }
    }

    if (urlInit.city) {
      const city = CITIES.find((ct) => ct.key === urlInit.city.toLowerCase());
      if (city) flyTo(city.lat, city.lng);
    }
  }, [loadCourts, flyTo, urlInit, hasUrlBounds, bounds, mapCenter, resolveUserLocation]);

  const currentCity = useMemo(() => {
    if (!mapCenter) return "";
    let closest = CITIES[0];
    let minDist = Infinity;
    for (const city of CITIES) {
      const d = Math.sqrt((city.lat - parseFloat(mapCenter.lat)) ** 2 + (city.lng - parseFloat(mapCenter.lng)) ** 2);
      if (d < minDist) { minDist = d; closest = city; }
    }
    return minDist < 0.5 ? closest.key : "";
  }, [mapCenter]);

  const hasTimeFilter = timeDate && timeFrom && timeTo;

  const presets = useMemo(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
    return [
      { label: t("search.time_today_evening"), date: todayStr, from: "18", to: "20" },
      { label: t("search.time_tomorrow_morning"), date: tomorrowStr, from: "8", to: "10" },
      { label: t("search.time_tomorrow_evening"), date: tomorrowStr, from: "18", to: "20" },
    ];
  }, [t]);

  const applyTimeFilter = useCallback((d, f, tVal) => {
    setTimeDate(d);
    setTimeFrom(f);
    setTimeTo(tVal);
    setShowTimeDialog(false);
    loadCourts(bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, d, f, tVal, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow);
  }, [bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, loadCourts, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow]);

  const clearTimeFilter = useCallback(() => {
    setTimeDate("");
    setTimeFrom("");
    setTimeTo("");
    loadCourts(bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, "", "", "", searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow);
  }, [bounds, mapCenter, sport, indoor, selectedAmenities, selectedPaymentMethods, sort, loadCourts, searchQuery, minPrice, maxPrice, courtType, courtSurface, openNow]);

  const hasActiveFilters = sport !== "all" || indoor !== "any" || selectedAmenities.length > 0 || selectedPaymentMethods.length > 0 || hasTimeFilter || !!searchQuery || minPrice || maxPrice || courtType || courtSurface || openNow;

  const clearAllFilters = useCallback(() => {
    setSport("all");
    setIndoor("any");
    setSelectedAmenities([]);
    setSelectedPaymentMethods([]);
    setTimeDate("");
    setTimeFrom("");
    setTimeTo("");
    setSearchQuery("");
    setMinPrice("");
    setMaxPrice("");
    setCourtType("");
    setCourtSurface("");
    setOpenNow(false);
    loadCourts(bounds, mapCenter, "all", "any", [], [], "", "", "", "", "", "", "", "", "", false);
  }, [bounds, mapCenter, loadCourts]);

  const zoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.flyTo([31.95, 35.93], 9, { duration: 1.5 });
    }
  }, []);

  const emptyTitle = hasTimeFilter
    ? t("search.empty_time_title")
    : sport !== "all"
    ? t("search.empty_sport_title", { sport: t("home.sports." + sport) })
    : hasActiveFilters
    ? t("search.empty_filters_title")
    : t("search.empty_area_title");

  const emptyHint = hasTimeFilter
    ? t("search.empty_time_hint")
    : sport !== "all"
    ? t("search.empty_sport_hint")
    : hasActiveFilters
    ? t("search.empty_filters_hint")
    : t("search.empty_area_hint");

  return (
    <div className="bg-background min-h-[calc(100vh-64px)] sm:h-[calc(100vh-64px)] flex flex-col">
      {/* Top bar */}
      <div className="flex items-center gap-2 px-3 md:px-5 py-2.5 border-b border-border overflow-x-auto shrink-0">
        <div className="flex items-center gap-1.5 bg-card border border-border px-3 py-1.5 shrink-0">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground/80" />
          <select
            value={currentCity}
            onChange={(e) => handleCitySelect(e.target.value)}
            className="bg-transparent text-xs tracking-wider uppercase text-foreground/80 focus:outline-none cursor-pointer max-w-[120px]"
          >
            <option value="" className="bg-card">{t("search.all_cities")}</option>
            {CITIES.map((c) => (
              <option key={c.key} value={c.key} className="bg-card">{t("cities." + c.key)}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1.5 bg-card border border-border px-3 py-1.5 shrink-0 min-w-0">
          <SearchIcon className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
          <input
             type="text"
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
             onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(e.target.value); }}
             placeholder={t("search.placeholder_search")}
             className="bg-transparent text-xs tracking-wider text-foreground/80 focus:outline-none w-[120px] placeholder:text-muted-foreground/60"
           />
        </div>

        <button
          onClick={handleNearMe}
          disabled={nearMeLoading}
          className="flex items-center gap-1.5 bg-card border border-border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors shrink-0 disabled:opacity-40"
        >
          <Navigation className={`h-3 w-3 ${nearMeLoading ? "animate-spin" : ""}`} />
          {t("search.near_me")}
        </button>

        <button
          onClick={() => setShowTimeDialog(true)}
          className={`flex items-center gap-1.5 bg-card border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-colors shrink-0 ${
            hasTimeFilter
              ? "border-volt text-volt"
              : "border-border text-muted-foreground hover:text-volt hover:border-volt/50"
          }`}
        >
          <Clock className="h-3 w-3" />
          {hasTimeFilter
            ? `${timeDate.slice(5)} ${timeFrom}:00–${timeTo}:00`
            : t("search.time")}
          {hasTimeFilter && (
            <span
              onClick={(e) => { e.stopPropagation(); clearTimeFilter(); }}
              className="ms-1 text-[9px] tracking-widest uppercase text-muted-foreground/80 hover:text-red-400 px-1"
            >
              {t("search.time_clear")}
            </span>
          )}
        </button>

        <div className="h-5 w-px bg-border/30 mx-1 shrink-0" />

        <select
          value={sport}
          onChange={(e) => handleSportChange(e.target.value)}
          className="bg-card border border-border text-[10px] tracking-widest uppercase px-3 py-1.5 focus:outline-none focus:border-volt cursor-pointer shrink-0"
          style={sport !== "all" ? { borderColor: hexToRgba(SPORT_COLORS[sport].bg, 0.6), color: hexToRgba(SPORT_COLORS[sport].bg, 1) } : {}}
        >
          <option value="all" className="bg-card">{t("search.all_sports")}</option>
          {SPORTS.filter((s) => s !== "all").map((s) => (
            <option key={s} value={s} className="bg-card">{t("home.sports." + s)}</option>
          ))}
        </select>

        <div className="h-5 w-px bg-border/30 mx-1 shrink-0" />

        <select
          value={sort}
          onChange={(e) => handleSortChange(e.target.value)}
          className="bg-card border border-border text-[10px] tracking-widest uppercase px-3 py-1.5 focus:outline-none focus:border-volt cursor-pointer shrink-0"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-card">{t(opt.label_key)}</option>
          ))}
        </select>

        <button
          onClick={() => setShowFilters(true)}
          className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-colors shrink-0 ${
            indoor !== "any" || selectedAmenities.length > 0 || selectedPaymentMethods.length > 0 || minPrice || maxPrice || courtType || courtSurface || openNow
              ? "bg-volt/10 border-volt text-volt"
              : "bg-card border-border text-muted-foreground hover:text-volt hover:border-volt/50"
          }`}
        >
          <SlidersHorizontal className="h-3 w-3" />
          {t("search.filters")}
          {(indoor !== "any" || selectedAmenities.length > 0 || selectedPaymentMethods.length > 0 || minPrice || maxPrice || courtType || courtSurface || openNow) && (
            <span className="w-1.5 h-1.5 rounded-full bg-volt" />
          )}
        </button>

        <div className="flex-1" />

        <button
          onClick={() => setPanelOpen(!panelOpen)}
          className="flex items-center gap-1.5 bg-card border border-border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors shrink-0 lg:hidden"
        >
          <SearchIcon className="h-3 w-3" />
          {total > 0 ? `${courts.length}/${total}` : "0"}
        </button>
      </div>

      {/* Map + Panel */}
      <div className="flex-1 flex flex-col sm:flex-row px-2 sm:px-6 pb-2 sm:pb-6 gap-2 sm:gap-4 sm:overflow-hidden">
        {/* Map — sticky top on mobile */}
        <div className="sm:flex-1 h-[40vh] sm:h-auto min-h-0 rounded-sm overflow-hidden sm:sticky sm:top-0">
          <MapView
            courts={courts}
            center={mapCenter}
            zoom={mapZoom}
            onBoundsChange={handleMove}
            onMapReady={handleMapReady}
            userLocation={userLocation}
          />
        </div>

        {/* Results panel — scrolls with page on mobile, scrolls independently on desktop */}
          <div className="flex-1 sm:flex-none sm:w-[420px] lg:w-1/2 bg-background/95 backdrop-blur border border-border rounded-sm flex flex-col sm:overflow-hidden min-h-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border sm:shrink-0">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground/80">
                {loading ? t("common.loading") : `${total} ${t("search.courts_found")}`}
              </span>
            </div>
            <div className="flex-1 sm:overflow-y-auto min-h-0">
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="border border-border bg-card overflow-hidden animate-pulse">
                    <div className="aspect-[4/3] bg-muted" />
                    <div className="p-4 space-y-2">
                      <div className="h-5 bg-muted w-2/3" />
                      <div className="h-3 bg-muted w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : courts.length === 0 ? (
              <div className="p-10 text-center">
                <MapPin className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
                <p className="font-heading text-xl text-muted-foreground/80">{emptyTitle}</p>
                <p className="text-sm text-muted-foreground/60 mt-1 mb-6">{emptyHint}</p>
                <div className="flex flex-col gap-2 items-center">
                  {hasActiveFilters && (
                    <button onClick={clearAllFilters} className="bg-volt text-primary-foreground px-5 py-2.5 text-[10px] tracking-widest uppercase font-bold hover:bg-white transition-colors">
                      {t("search.empty_clear_filters")}
                    </button>
                  )}
                  <button onClick={zoomOut} className="btn-outline rounded-sm px-5 py-2.5 text-[10px] tracking-widest uppercase font-bold">
                    {t("search.empty_zoom_out")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-2 sm:p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
                  {courts.map((c, i) => (
                    <CourtCard key={c.id} court={c} index={i} distanceKm={c.distance_km} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time filter dialog */}
      {showTimeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => setShowTimeDialog(false)}>
          <div className="bg-card border border-border w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <span className="label-eyebrow flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" /> {t("search.time_title")}
              </span>
              <button onClick={() => setShowTimeDialog(false)} className="text-muted-foreground/80 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {presets.map((p, i) => (
                <button
                  key={i}
                  onClick={() => applyTimeFilter(p.date, p.from, p.to)}
                  className={`px-3 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                    timeDate === p.date && timeFrom === p.from && timeTo === p.to
                      ? "border-volt text-volt bg-volt/10"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("search.time_date")}</label>
                <input
                  type="date"
                  value={timeDate}
                  onChange={(e) => setTimeDate(e.target.value)}
                  className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("search.time_from")}</label>
                  <select
                    value={timeFrom}
                    onChange={(e) => setTimeFrom(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none cursor-pointer"
                  >
                    <option value="" className="bg-card">-</option>
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={String(h)} className="bg-card">{h}:00</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-1 block">{t("search.time_to")}</label>
                  <select
                    value={timeTo}
                    onChange={(e) => setTimeTo(e.target.value)}
                    className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none cursor-pointer"
                  >
                    <option value="" className="bg-card">-</option>
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={String(h)} className="bg-card">{h}:00</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => { setTimeDate(""); setTimeFrom(""); setTimeTo(""); }}
                className="btn-outline rounded-sm px-5 py-3 text-xs flex-1"
              >
                {t("search.reset")}
              </button>
              <button
                onClick={() => applyTimeFilter(timeDate, timeFrom, timeTo)}
                disabled={!timeDate || !timeFrom || !timeTo}
                className="btn-volt rounded-sm px-5 py-3 text-xs flex-1 disabled:opacity-30"
              >
                {t("search.apply")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => setShowFilters(false)}>
          <div className="bg-card border border-border w-full max-w-md mx-4 p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <span className="label-eyebrow flex items-center gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" /> {t("search.advanced_filters")}
              </span>
              <button onClick={() => setShowFilters(false)} className="text-muted-foreground/80 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-5">
              {/* Open Now */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className={`relative w-10 h-5 rounded-full transition-colors ${openNow ? "bg-volt" : "bg-border/30"}`} onClick={() => setOpenNow(!openNow)}>
                    <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${openNow ? "translate-x-5" : "translate-x-0.5"}`} />
                  </div>
                  <div>
                    <span className="text-[10px] tracking-widest uppercase font-bold text-foreground/80 group-hover:text-foreground block">{t("search.open_now")}</span>
                    <span className="text-[10px] text-muted-foreground/80">{t("search.open_now_desc")}</span>
                  </div>
                </label>
              </div>

              {/* Indoor/Outdoor */}
              <div>
                <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.indoor_filter")}</label>
                <div className="flex gap-2">
                  {[
                    { v: "any", l: t("search.indoor_any") },
                    { v: "indoor", l: t("search.indoor_yes") },
                    { v: "outdoor", l: t("search.indoor_no") },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => setIndoor(opt.v)}
                      className={`px-4 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                        indoor === opt.v
                          ? "border-volt text-volt bg-volt/10"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.price_range")}</label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min={0}
                      placeholder={t("search.price_min")}
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:border-volt focus:outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground/60 tracking-wider uppercase pointer-events-none">{t("search.price_per_hour", { currency: "JD" })}</span>
                  </div>
                  <span className="text-muted-foreground/40">—</span>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min={0}
                      placeholder={t("search.price_max")}
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:border-volt focus:outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground/60 tracking-wider uppercase pointer-events-none">{t("search.price_per_hour", { currency: "JD" })}</span>
                  </div>
                </div>
              </div>

              {/* Court Type */}
              {sport !== "all" && (COURT_TYPES[sport] || []).length > 0 && (
                <div>
                  <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.court_type_filter")}</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setCourtType("")}
                      className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                        !courtType
                          ? "border-volt text-volt bg-volt/10"
                          : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      {t("search.court_type_any")}
                    </button>
                    {(COURT_TYPES[sport] || []).map((ct) => (
                      <button
                        key={ct.value}
                        onClick={() => setCourtType(courtType === ct.value ? "" : ct.value)}
                        className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                          courtType === ct.value
                            ? "border-volt text-volt bg-volt/10"
                            : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                        }`}
                      >
                        {t(ct.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Court Surface */}
              {sport !== "all" && (COURT_SURFACES[sport] || []).length > 0 && (
                <div>
                  <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.court_surface_filter")}</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setCourtSurface("")}
                      className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                        !courtSurface
                          ? "border-volt text-volt bg-volt/10"
                          : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      {t("search.court_surface_any")}
                    </button>
                    {(COURT_SURFACES[sport] || []).map((cs) => (
                      <button
                        key={cs.value}
                        onClick={() => setCourtSurface(courtSurface === cs.value ? "" : cs.value)}
                        className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                          courtSurface === cs.value
                            ? "border-volt text-volt bg-volt/10"
                            : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                        }`}
                      >
                        {t(cs.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Methods */}
              <div>
                <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.payment_methods_filter")}</label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSelectedPaymentMethods([])}
                    className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                      selectedPaymentMethods.length === 0
                        ? "border-volt text-volt bg-volt/10"
                        : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                    }`}
                  >
                    {t("search.payment_methods_any")}
                  </button>
                  {PAYMENT_METHOD_OPTIONS.map((method) => {
                    const active = selectedPaymentMethods.includes(method.value);
                    return (
                      <button
                        key={method.value}
                        onClick={() => setSelectedPaymentMethods(
                          active
                            ? selectedPaymentMethods.filter((value) => value !== method.value)
                            : [...selectedPaymentMethods, method.value]
                        )}
                        className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                          active
                            ? "border-volt text-volt bg-volt/10"
                            : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                        }`}
                      >
                        {t(method.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="text-[9px] tracking-widest uppercase text-muted-foreground mb-2 block">{t("search.amenities_filter")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {allAmenities.map((a) => {
                    const active = selectedAmenities.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        onClick={() => setSelectedAmenities(
                          active ? selectedAmenities.filter((k) => k !== a.key) : [...selectedAmenities, a.key]
                        )}
                        className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                          active
                            ? "border-volt text-volt bg-volt/10"
                            : "border-border text-muted-foreground/80 hover:text-foreground hover:border-foreground/30"
                        }`}
                      >
                        {t(`owner.amenities.${a.key}`, a.label || a.key)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => { setIndoor("any"); setSelectedAmenities([]); setSelectedPaymentMethods([]); setMinPrice(""); setMaxPrice(""); setCourtType(""); setCourtSurface(""); setOpenNow(false); }}
                className="btn-outline rounded-sm px-5 py-3 text-xs flex-1"
              >
                {t("search.reset")}
              </button>
              <button
                onClick={() => applyFilters(indoor, selectedAmenities, selectedPaymentMethods, minPrice, maxPrice, courtType, courtSurface, openNow)}
                className="btn-volt rounded-sm px-5 py-3 text-xs flex-1"
              >
                {t("search.apply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
